import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcK = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule K Explained: Everything You Need to Know"
          description="Filing Form 990 Schedule K? This guide provides comprehensive information on reporting tax-exempt bonds for your nonprofit organization."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-k/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule K</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Simplify Tax-Exempt Bond Reporting with TaxZerone!
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-k/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule K at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Purpose: Form 990 Schedule K</b> is used to report{' '}
                        <b>
                          supplemental information on tax-exempt bond issues
                        </b>
                        , including outstanding bond liabilities and compliance
                        with IRS tax-exempt bond requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must File?</b> Organizations that answer{' '}
                        <b>"Yes"</b> to <b>Form 990, Part IV, line 24a</b>{' '}
                        generally must complete and attach <b>Schedule K</b>.
                        This typically applies to organizations with{' '}
                        <b>tax-exempt bond issues exceeding $100,000</b> in
                        outstanding principal at the end of the tax year that
                        were <b>issued after December 31, 2002</b>.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>What Is Reported?</b> Schedule K reports{' '}
                        <b>
                          bond issue details, use of bond proceeds, private
                          business use, arbitrage compliance
                        </b>
                        , and ongoing tax-exempt bond compliance information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Multiple Bond Issues:</b> A single <b>Schedule K</b>{' '}
                        can report <b>up to four tax-exempt bond issues</b>.
                        Organizations with additional bond issues must attach{' '}
                        <b>separate Schedule K</b> forms.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule K?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule K?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule K Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule K filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule K?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>Schedule K, </b>
                  is a supplemental schedule attached to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>{' '}
                  to provide certain information on its{' '}
                  <b>
                    outstanding liabilities associated with tax-exempt bond
                    issues
                  </b>
                  . Municipalities typically issue these bonds to raise funds
                  for qualified organizations, allowing the organization to
                  access capital for projects at a lower interest rate.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule K?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An organization that answered “Yes” on{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 990
                  </Link>
                  , Part IV, Checklist of Required Schedules, question 24a, must
                  complete and attach Schedule K to Form 990. This applies to
                  most types of tax-exempt bonds used by qualified
                  organizations, not just the common 501(c)(3) bonds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  It also indicates the organization reported an outstanding
                  tax-exempt bond issue that:
                </Typography>
                <Box component="ul" sx={{ mb: 3 }}>
                  <li>
                    Had an outstanding principal amount of over $100,000 as of
                    the last day of the tax year, and
                  </li>
                  <li>Was issued after December 31, 2002.</li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note</b> - An organization can report up to four separate
                  outstanding tax-exempt liabilities on each Schedule K (Form
                  990). The schedule can be duplicated if needed to report more
                  than four tax-exempt liabilities.
                </Alert>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <b>
                      Embrace the Future of Tax Filing – Choose TaxZerone Today!
                    </b>
                    <br />
                    Make your e-filing process simple by clicking the button
                    below.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schk`}
                  >
                    Start E-filing Now
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="scha"
                >
                  Schedule K Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-details-img.webp" />
                  <source srcSet="/assets/images/f990-schk-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-details-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K showing organization details and basic information"
                    width={760}
                    height={97}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule K filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Bond Issues
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schk-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K- Part I Details of Supplemental Information"
                    width={760}
                    height={135}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To complete Part I, provide the requested information for each
                  outstanding tax-exempt bond issue (including a refunding
                  issue) that:
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li>
                    Had an outstanding principal amount of over $100,000 as of
                    the last day of the tax year (or other selected 12-month
                    period), and
                  </li>
                  <li>Was issued after December 31, 2002</li>
                </Box>
                <Box component="ol" sx={{ mb: 4 }}>
                  <li>
                    Columns (a) and (b) - Enter the name and employer
                    identification number (EIN) of the issuer of the bond issue.
                  </li>
                  <li>
                    Column (c) - Enter the Committee on Uniform Securities
                    Identification Procedures (CUSIP) number on the bond with
                    the latest maturity. <br />
                    The CUSIP number should be identical to the CUSIP number
                    listed on Form 8038, Part I, line 9, filed for the bond
                    issue. If the bond issue wasn&apos;t publicly offered and
                    there is no assigned CUSIP number, enter zeros in place of
                    the CUSIP number.
                  </li>
                  <li>
                    Column (d) - Enter the issue date of the obligation. The
                    issue date should be identical to the issue date listed on
                    Form 8038, Part I, line 7, filed for the bond issue.
                  </li>
                  <li>
                    Column (e) - Enter the issue price of the obligation. The
                    issue price should generally be identical to the issue price
                    listed on Form 8038, Part III, line 21(b), filed for the
                    bond issue.
                  </li>
                  <li>Column (f) - Describe the purpose of the bond issue.</li>
                  <li>
                    Column (g) - Check “Yes” or “No” to indicate whether a
                    defeasance escrow or refunding escrow has been established
                    to irrevocably defease any bonds of the bond issue
                  </li>
                  <li>
                    Column (h). Check “Yes” if the organization acted as an “on
                    behalf of issuer” in issuing the bond issue. Check “No” if
                    the organization only acted as the borrower of the bond
                    proceeds under the terms of a conduit loan with the
                    governmental issuer of the bond issue.
                  </li>
                  <li>
                    Column (i) - Check “Yes” or “No” to indicate if the bond
                    issue was a pooled financing issue.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Proceeds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schk-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K, Part II, reporting use of bond proceeds, allocations, and related expenditure details."
                    width={760}
                    height={275}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total principal amount
                  </span>{' '}
                  of bonds that have been fully paid off or retired by the end
                  of the 12-month reporting period.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total principal amount of bonds that are not yet
                  paid off but have been legally set aside for repayment through
                  a defeasance or refunding escrow.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total bond proceeds received as of the end of the
                  12-month period. If this amount differs from the issue price
                  shown earlier, explain the reason in Part VI (for example,
                  investment earnings).{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of bond proceeds held in required reserve,
                  sinking, pledged, or replacement funds at the end of the
                  12-month period.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total bond proceeds used{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    to pay interest costs
                  </span>
                  during construction of the financed capital project.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of bond proceeds held in a refunding escrow
                  account at the end of the 12-month period, including related
                  investment earnings.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total proceeds used to pay bond issuance costs, such
                  as underwriting fees, trustee fees, or bond counsel fees.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the proceeds used to pay credit enhancement fees, such
                  as bond insurance premiums or letter-of-credit fees.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the proceeds used for working capital expenses (regular
                  operating costs), excluding amounts already reported on lines
                  4, 6, 7, or 8.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the proceeds used for capital expenditures, such as
                  buying, building, or improving land, buildings, or equipment.
                  Don’t include capitalized interest already reported on line
                  5.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter proceeds used for any other purpose not reported on
                  lines 4–10, including amounts used to redeem or legally
                  defense bonds.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of unspent bond proceeds remaining at the end
                  of the 12-month period, excluding amounts already reported on
                  lines 4, 6, and 11.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the year the financed project was substantially
                  completed, meaning it was ready and operating close to its
                  intended design level. If multiple projects were financed,
                  enter the latest completion year.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the bonds were issued after 2017 to refund
                  tax-exempt bonds, or before 2018 to current refund tax-exempt
                  bonds. Otherwise, check “No.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the bonds were issued after 2017 to refund
                  taxable bonds, or before 2018 to advance refund tax-exempt
                  bonds. Otherwise, check “No.”{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” or “No” to show whether the final allocation of
                  bond proceeds has been completed using a consistent accounting
                  method.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Check “Yes” or “No” to confirm whether the organization keeps
                  proper books and records supporting the final allocation of
                  bond proceeds.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Private Business Use
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schk-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K, Part III, reporting private business use of bond-financed property and related compliance details."
                    width={760}
                    height={365}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete this part for each applicable tax-exempt bond issue
                  listed in Part I (rows A–D), except certain older refunding
                  bond issues. Use additional schedules if needed to report all
                  bond issues.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Some qualified private activity bonds (other than qualified
                  501(c)(3) bonds) don’t need detailed reporting here, but you
                  must identify them in Part VI.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if, during the year, the organization was a
                  partner in a partnership or member of an LLC that owned
                  property financed by the bonds and included non-501(c)(3)
                  entities as partners or members. Otherwise, check “No.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if any lease or rental agreements existed during
                  the year for property financed with bond proceeds that allowed
                  private or nongovernmental use. Otherwise, check “No.”{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check “Yes” if there was any management or service contract
                  during the year related to property financed by the bonds that
                  could allow private business use.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if there was any management or service contract
                  during the year related to property financed by the bonds that
                  could allow private business use.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If line 3a is “Yes,” indicates whether the organization
                  regularly used bond counsel or outside legal advisors to
                  review those management or service contracts.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if there is any research agreement involving
                  bond-financed property that could result in private business
                  use, even if it meets safe-harbor rules.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If line 3c is “Yes,” indicates whether the organization
                  regularly used bond counsel or outside legal advisors to
                  review those research agreements.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the average percentage of bond-financed property used
                    by private or nongovernmental parties (other than 501(c)(3)
                    organizations) during the year.
                  </li>
                  <li>
                    Do not include uses that qualify under safe-harbor
                    management/service contracts or research agreements.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the average percentage of the property used for
                  unrelated trade or business activities, even if used by your
                  organization, another 501(c)(3), or a government unit.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total of lines 4 and 5.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Check “Yes” if the bond issue meets the private security or
                    payment test under sections 141(b)(2) and 145.
                  </li>
                  <li>
                    This generally means that no more than 5% of bond payments
                    or property were used or secured for private business
                    purposes.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if any of the financed property was sold or
                  transferred to someone other than a government unit or another
                  501(c)(3) organization.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If 8a is “Yes,” enter the cumulative percentage of property
                  sold or transferred since the bonds were issued.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If 8a is “Yes,” indicate whether the organization took
                  remedial actions for any nonqualified bonds resulting from the
                  transfer.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Check “Yes” if the organization has written procedures to
                  ensure timely remedial actions for all nonqualified bonds,
                  following IRS rules and regulations.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV - Arbitrage
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-part4-img.webp" />
                  <source srcSet="/assets/images/f990-schk-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K, Part IV, lines 1–3, reporting arbitrage compliance, rebate requirements, and related bond information."
                    width={760}
                    height={135}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0.5,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Interest on tax-exempt bonds isn’t fully tax-exempt unless the
                  issuer pays back any excess earnings (arbitrage profits) to
                  the U.S. Treasury.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the issuer has filed the most recent Form
                  8038-T for arbitrage rebate or related payments; otherwise,
                  check “No.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the issuer did not file Form 8038-T because a
                  filing was required but not yet completed for the most recent
                  computation date.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the issuer did not file Form 8038-T because a
                  filing was not required for some special reason (for example,
                  a small bond exception or other IRS exemption).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the issuer did not file Form 8038-T because no
                  rebate was due for that computation date. If this applies,
                  provide the rebate computation date in Part VI.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Check “Yes” if the bond issue is a variable rate bond, meaning
                  the interest rate isn’t fixed or known at the time the bonds
                  were issued. Otherwise, check “No.”{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-part4-1-img.webp" />
                  <source srcSet="/assets/images/f990-schk-part4-1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-part4-1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K – Part IV: Arbitrage compliance and related calculations."
                    width={760}
                    height={181}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the organization (or governmental issuer) has
                  entered a qualified hedge on the bond proceeds and recorded it
                  in the issuer’s books. Otherwise, check “No”. A qualified
                  hedge is a financial contract used to manage interest rate
                  risk, which affects the bond yield.{' '}
                </Typography>{' '}
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  If 4a is “Yes,” enter the name of the hedge provider (the
                  company or institution that issued the hedge).{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the length of the hedge in years, rounded to the nearest
                  tenth (e.g., 2.4 years).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the hedge changes the treatment of variable
                  rate bonds to fixed yield bonds (called “super integration”).
                  Otherwise, check “No.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the hedge ended early, before its scheduled
                  termination date. Otherwise, check “No.”{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if any bond proceeds were invested in a guaranteed
                  investment contract (GIC) a specially negotiated investment
                  with set terms and interest rates. Otherwise, check “No.”{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If 5a is “Yes,” enter the name of the GIC provider.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the term (length) of the GIC in years, rounded to the
                  nearest tenth (e.g., 2.5 years).{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the regulatory safe harbor for fair market
                  value was met; otherwise, check “No.”{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if any gross proceeds were invested longer than
                  allowed temporary periods, or if any proceeds in a
                  reserve/replacement fund exceeded limits. Otherwise, check
                  “No.”{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if the organization has written procedures to
                  monitor compliance with arbitrage, yield restriction, and
                  rebate rules under section 148. Otherwise, check “No.”{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V - Procedures To Undertake Corrective Action
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-part5-img.webp" />
                  <source srcSet="/assets/images/f990-schk-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-part5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K – Part V: Procedures to Undertake Corrective Action"
                    width={760}
                    height={74}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” or “No” to indicate whether the organization has
                  established written procedures to ensure timely identification
                  of violations of federal tax requirements and timely
                  correction of any identified violation(s) through the use of
                  the voluntary closing agreement program if self-remediation
                  isn&apos;t available under applicable regulations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Answer “Yes” only if the procedures applied during the 12
                  months are used to report on the bond issue.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VI - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schk-part6-img.webp" />
                  <source srcSet="/assets/images/f990-schk-part6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schk-part6-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule K- Part VI Details of Supplemental Information"
                    width={760}
                    height={283}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Part VI to provide the narrative explanations required, if
                  applicable, to supplement Part I, columns (e) and (f); to
                  provide additional information or comments relating to the
                  reporting of liabilities by related organizations; and to
                  describe certain assumptions that are used to complete
                  Schedule K (Form 990) when the information provided isn&apos;t
                  fully supported by existing records.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Also, use this part to supplement responses to questions on
                  Schedule K (Form 990). Identify the specific part and line
                  number that the response supports, in the order in which the
                  responses appear on Schedule K (Form 990).
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule K filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>TaxZerone</b> is an IRS-authorized e-file service provider,
                  that offers more than a seamless e-filing experience. By
                  filing with us, you get instant updates on your exempt
                  information return’s filing status, ensuring you&apos;re
                  always in the loop.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  At TaxZerone, we&apos;ve designed a user-friendly platform
                  that simplifies the e-filing process and provides
                  comprehensive support at every step.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here’s how your Form 990 return with Schedule K attachment is
                  transmitted to the IRS in 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Provide Organization Details -</b> Choose the tax year
                    for which you want to file a return, and provide your
                    organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Preview the Return -</b> Complete Schedule K and preview
                    the information provided in the return for accuracy before
                    transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Transmit to the IRS -</b> Transmit Schedule K and your{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      990 return
                    </Link>{' '}
                    to the IRS and get the acceptance in just a few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In case of IRS rejection for your 990 information return,
                  worry not! We offer a hassle-free correction and
                  retransmission process, absolutely free of charge!
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Choose TaxZerone - Your Gateway to Seamless Tax Filing
                      </Typography>
                      <Typography variant="body2">
                        Complete your Form 990 return and Schedule K filing
                        requirements with ease!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schk`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990sk.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule K (Form 990) – Supplemental Information
                          on Tax-Exempt Bonds
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What types of transactions must reported on Schedule K?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule K requires reporting on various types of tax-exempt
                    financing transactions, including bond issues, refunding
                    bond issues, private activity bond issues, and advance
                    refunding, among others.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How many bond issues can be reported on one Schedule K?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Up to{' '}
                    <b>four separate outstanding tax-exempt liabilities</b> can
                    be reported on a single Schedule K. If your exempt
                    organization has more than four, they can duplicate the form
                    to report on all their bonds.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Do all tax-exempt organizations need to file Schedule K?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    No, only organizations that have engaged in tax-exempt bond
                    issues or certain other tax-exempt financing transactions
                    during the tax year need to file Schedule K along with their{' '}
                    Form 990.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcK;
